import { useEffect, useState } from "react";

import { API_BASE_URL } from "../config";

import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import {
  BarChart as MuiBarChart,
  PieChart as MuiPieChart,
} from "@mui/x-charts";

const AdminDashboard = () => {
  const [summary, setSummary] = useState({
    totalUsers: 0,
    totalProjects: 0,
    totalComments: 0,
    todoTasks: 0,
    inProgressTasks: 0,
    completedTasks: 0,
  });
  const [activity, setActivity] = useState([]);
  const [hours, setHours] = useState([]);
  // State for total hours/minutes from /api/admin/time-logs
  const [totalHours, setTotalHours] = useState(0);
  const [totalMinutes, setTotalMinutes] = useState(0);

  const [orderActivity, setOrderActivity] = useState("asc");
  const [orderByActivity, setOrderByActivity] = useState("name");

  const [orderHours, setOrderHours] = useState("asc");
  const [orderByHours, setOrderByHours] = useState("name");

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    const isVerified = localStorage.getItem("isVerified");

    if (!token || !role) {
      navigate("/login");
    } else if (isVerified !== "true") {
      navigate("/verify");
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_BASE_URL}/api/admin/dashboard-summary`, {
        headers,
        credentials: "include",
      }),
      fetch(`${API_BASE_URL}/api/admin/time-logs`, {
        headers,
        credentials: "include",
      }),
      fetch(`${API_BASE_URL}/api/admin/recent-activity`, {
        headers,
        credentials: "include",
      }),
      fetch(`${API_BASE_URL}/api/admin/last-logged-hours`, {
        headers,
        credentials: "include",
      }),
      fetch(`${API_BASE_URL}/api/admin/tasks`, {
        headers,
        credentials: "include",
      }),
    ])
      .then(async ([summaryRes, logsRes, activityRes, hoursRes, tasksRes]) => {
        const summaryData = await summaryRes.json();
        const logsData = await logsRes.json();
        const activityData = await activityRes.json();
        const hoursData = await hoursRes.json();
        const tasksData = await tasksRes.json();

        const count = {
          "To Do": 0,
          "In Progress": 0,
          Completed: 0,
        };
        tasksData.forEach((task) => {
          if (count[task.status] !== undefined) {
            count[task.status]++;
          }
        });

        setSummary({
          totalUsers: summaryData.totalUsers || 0,
          totalProjects: summaryData.totalProjects || 0,
          totalComments: summaryData.totalComments || 0,
          todoTasks: count["To Do"],
          inProgressTasks: count["In Progress"],
          completedTasks: count["Completed"],
        });

        setTotalHours(logsData.totalHours || 0);
        setTotalMinutes(logsData.totalMinutes || 0);
        setActivity(Array.isArray(activityData) ? activityData : []);
        setHours(Array.isArray(hoursData.logs) ? hoursData.logs : []);
      })
      .catch((err) => {
        console.error("Failed to load dashboard data:", err);
      });
  }, [navigate]);

  const handleSortActivity = (property) => {
    const isAsc = orderByActivity === property && orderActivity === "asc";
    setOrderActivity(isAsc ? "desc" : "asc");
    setOrderByActivity(property);
  };

  const sortedActivity = [...activity].sort((a, b) => {
    if (a[orderByActivity] < b[orderByActivity])
      return orderActivity === "asc" ? -1 : 1;
    if (a[orderByActivity] > b[orderByActivity])
      return orderActivity === "asc" ? 1 : -1;
    return 0;
  });

  const handleSortHours = (property) => {
    const isAsc = orderByHours === property && orderHours === "asc";
    setOrderHours(isAsc ? "desc" : "asc");
    setOrderByHours(property);
  };

  const sortedHours = [...hours].sort((a, b) => {
    if (a[orderByHours] < b[orderByHours]) return orderHours === "asc" ? -1 : 1;
    if (a[orderByHours] > b[orderByHours]) return orderHours === "asc" ? 1 : -1;
    return 0;
  });

  const taskData = [
    { name: "To Do", value: summary.todoTasks || 0 },
    { name: "In Progress", value: summary.inProgressTasks || 0 },
    { name: "Completed", value: summary.completedTasks || 0 },
  ];

  const COLORS = ["#7f30cb", "#0051ffff", "#01dcba"];

  // Responsive pie chart sizing
  const pieWidth = isMobile ? 280 : 300;
  const pieHeight = isMobile ? 220 : 240;
  const outerRadius = isMobile ? 80 : 100;
  const innerRadius = isMobile ? 24 : 30;
  const piePadding = isMobile ? 2 : 4;

  return (
    <>
      <Box className="admin-deshboard">
        <Typography variant="h5" fontWeight={600} mb={3}>
          Dashboard
        </Typography>

        <Box
          className="date-chart"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={4}
          mb={10}
          mt={10}
          flexWrap="wrap"
        >
          <Box
            flex="1"
            height={240}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="flex-start"
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              width: "100%",
              maxWidth: "100%",
              alignSelf: "flex-start",
            }}
          >
            <MuiBarChart
              series={[
                {
                  data: [
                    summary.totalUsers || 0,
                    summary.totalProjects || 0,
                    totalHours + totalMinutes / 60,
                    summary.totalComments || 0,
                  ],
                  barSize: 40,
                  color: "var(--primary-color)",
                },
              ]}
              xAxis={[
                {
                  data: ["Users", "Projects", "Hours", "Comments"],
                  scaleType: "band",
                },
              ]}
              yAxis={[
                {
                  scaleType: "linear",
                  min: 0, // ensures chart starts from 0
                },
              ]}
              layout="vertical"
              height={300}
              sx={{
                width: "100%",
                borderRadius: 5,
                backgroundColor: "#fff",
              }}
            />
          </Box>

          <Box
            flex="1"
            height={pieHeight}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Task Status Breakdown
            </Typography>
            <MuiPieChart
              series={[
                {
                  data: taskData.map((d, index) => ({
                    id: index,
                    value: d.value,
                    label: `${d.name}: ${d.value} `,
                    color: COLORS[index % COLORS.length] || "#ccc",
                  })),
                  innerRadius: innerRadius,
                  outerRadius: outerRadius,
                  paddingAngle: piePadding,
                  cornerRadius: 4,
                  startAngle: 0,
                  endAngle: 360,
                },
              ]}
              width={pieWidth}
              height={pieHeight}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 5,
              }}
              slotProps={{
                legend: {
                  direction: isMobile ? "row" : "column",
                  position: { vertical: "bottom", horizontal: "middle" },
                  padding: 4,
                },
              }}
            />
          </Box>
        </Box>

        {/* Recent Activity Table */}
        <Typography variant="h6" mb={1} mt={5}>
          Recent Activity
        </Typography>
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={
                    orderByActivity === "name" ? orderActivity : false
                  }
                >
                  <TableSortLabel
                    active={orderByActivity === "name"}
                    direction={
                      orderByActivity === "name" ? orderActivity : "asc"
                    }
                    onClick={() => handleSortActivity("name")}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByActivity === "activity_type" ? orderActivity : false
                  }
                >
                  <TableSortLabel
                    active={orderByActivity === "activity_type"}
                    direction={
                      orderByActivity === "activity_type"
                        ? orderActivity
                        : "asc"
                    }
                    onClick={() => handleSortActivity("activity_type")}
                  >
                    Activity
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByActivity === "project" ? orderActivity : false
                  }
                >
                  <TableSortLabel
                    active={orderByActivity === "project"}
                    direction={
                      orderByActivity === "project" ? orderActivity : "asc"
                    }
                    onClick={() => handleSortActivity("project")}
                  >
                    Project
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByActivity === "time" ? orderActivity : false
                  }
                >
                  <TableSortLabel
                    active={orderByActivity === "time"}
                    direction={
                      orderByActivity === "time" ? orderActivity : "asc"
                    }
                    onClick={() => handleSortActivity("time")}
                  >
                    Time
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedActivity.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No activity.
                  </TableCell>
                </TableRow>
              ) : (
                sortedActivity.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div>{a.name}</div>
                      <Typography variant="caption" color="text.secondary">
                        {a.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.activity_type}</TableCell>
                    <TableCell>
                      {a.project_title || a.project || "N/A"}
                    </TableCell>
                    <TableCell>{a.timestamp}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Last Logged Hours Table */}
        <Typography variant="h6" mb={1} mt={5}>
          Last Logged Hours
        </Typography>

        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={orderByHours === "name" ? orderHours : false}
                >
                  <TableSortLabel
                    active={orderByHours === "name"}
                    direction={orderByHours === "name" ? orderHours : "asc"}
                    onClick={() => handleSortHours("name")}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByHours === "project" ? orderHours : false
                  }
                >
                  <TableSortLabel
                    active={orderByHours === "project"}
                    direction={orderByHours === "project" ? orderHours : "asc"}
                    onClick={() => handleSortHours("project")}
                  >
                    Project
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByHours === "start_time" ? orderHours : false
                  }
                >
                  <TableSortLabel
                    active={orderByHours === "start_time"}
                    direction={
                      orderByHours === "start_time" ? orderHours : "asc"
                    }
                    onClick={() => handleSortHours("start_time")}
                  >
                    Start
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByHours === "end_time" ? orderHours : false
                  }
                >
                  <TableSortLabel
                    active={orderByHours === "end_time"}
                    direction={orderByHours === "end_time" ? orderHours : "asc"}
                    onClick={() => handleSortHours("end_time")}
                  >
                    End
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    orderByHours === "duration" ? orderHours : false
                  }
                >
                  <TableSortLabel
                    active={orderByHours === "duration"}
                    direction={orderByHours === "duration" ? orderHours : "asc"}
                    onClick={() => handleSortHours("duration")}
                  >
                    Duration
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHours.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No logs.
                  </TableCell>
                </TableRow>
              ) : (
                sortedHours.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell>{h.name}</TableCell>
                    <TableCell>
                      {h.project_title || h.project || "N/A"}
                    </TableCell>
                    <TableCell>{h.start_time}</TableCell>
                    <TableCell>{h.end_time}</TableCell>
                    <TableCell>{h.duration} min</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </>
  );
};

export default AdminDashboard;
